import { useState } from 'react';
import { api } from '@aeon-karaoke/cloud-convex';
import { Button, HandleField, Input, ProfilePicture, Toggle } from '@/component';
import type { CurrentUser } from '@/hook/useCurrentUser';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { DeleteAccountModal } from './Settings/DeleteAccountModal';
import { DiscoverabilityRows } from './Settings/DiscoverabilityRows';
import { FeedbackRow } from './Settings/FeedbackRow';

type SaveState = { kind: 'idle' } | { kind: 'saved' } | { kind: 'error'; message: string };

/** Settings, Profile: name, handle, email, library sharing and the picture.
 * One Save writes the text fields and the toggle together; the discovery
 * switches below it write on their own. Then the feedback form, when the
 * deployment names one, and Delete account last behind its own confirmation. */
export function SettingsProfileTab({ user }: { user: CurrentUser }) {
  const [name, setName] = useState(user.displayName ?? '');
  const [handle, setHandle] = useState(user.handle ?? '');
  const [share, setShare] = useState(user.shareLibraryInSession ?? false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const [deleting, setDeleting] = useState(false);

  async function save() {
    setBusy(true);
    setState({ kind: 'idle' });
    const result = await toResult(() =>
      convex.mutation(api.users.updateProfile, { displayName: name, handle, shareLibraryInSession: share })
    );
    setBusy(false);
    setState(result.ok ? { kind: 'saved' } : { kind: 'error', message: result.error.message });
  }

  return (
    <div className="mt-10 grid items-start gap-16 md:grid-cols-[minmax(0,1fr)_280px]">
      <div className="flex max-w-[520px] flex-col gap-[22px]">
        <Input label="Name" value={name} onChange={(event) => setName(event.target.value)} maxLength={32} />
        <HandleField
          value={handle}
          onChange={setHandle}
          currentHandle={user.handle}
          hint="You can change this once every 30 days."
        />
        <Input label="Email" value={user.email ?? ''} readOnly className="text-mute" />
        <div className="mt-[6px] flex items-start justify-between gap-6 border-t border-stripeA py-[18px]">
          <div>
            <div className="text-[15px] font-semibold">Share my library in sessions</div>
            <p className="mt-1 max-w-[44ch] text-sm leading-[1.5] text-mute">
              Others in a session can queue tracks you have access to.
            </p>
          </div>
          <Toggle checked={share} onChange={setShare} label="Share my library in sessions" />
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={() => void save()} disabled={busy} className="px-[19px]">
            Save changes
          </Button>
          {state.kind === 'saved' && <span className="text-[13px] text-up">Saved</span>}
          {state.kind === 'error' && <span className="text-[13px] text-down">{state.message}</span>}
        </div>
        <DiscoverabilityRows user={user} />
        <FeedbackRow />
        <div className="mt-[22px] flex items-start justify-between gap-6 border-t border-stripeA py-[18px]">
          <div>
            <div className="text-[15px] font-semibold">Delete account</div>
            <p className="mt-1 max-w-[44ch] text-sm leading-[1.5] text-mute">
              Removes your sign-in, profile and friends. This cannot be undone.
            </p>
          </div>
          <Button variant="outline" size="sm" quiet onClick={() => setDeleting(true)}>
            Delete account
          </Button>
        </div>
        <DeleteAccountModal open={deleting} subscribed={!!user.stripeSubscriptionId} onClose={() => setDeleting(false)} />
      </div>
      <ProfilePicture user={user} layout="column" />
    </div>
  );
}
